<script lang="ts">
  // (c) Cognisoft Aps, 2022 for Svelte 5: 2026
  // sentence is played, one picture to be selected
  // red and green frame for correct and error
  // hierarchies of help levels after error and with timed help

  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import { HelpLevel } from "../classes";

  import { afterCorrectWaitSecs, firstWaitSecs } from "../constants";

  import { Icons } from "../utilities/icons";
  import { shuffleArray } from "../utilities/shuffleArray";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { targetVerbAndObjects } from "../globals/targets";
  import { route } from "../stores/route.svelte";
  import { progress } from "../stores/progress.svelte";

  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audio-tag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // CONSTANTS ------------------------------------------------

  const numberOfHelpLevels = 5;
  // const numberOfStages = 4;

  // STATE ------------------------------------------------

  // complicated currentState because of levels of help both timed and requested

  let currentState: State = $state(State.WaitToStart); // currentState for task solution for each item/picture
  let stage: 0 | 1 | 2 | 3 = $state(0); // 0-3, one stage for each sentence presented
  let targets = $state([0, 1, 2, 3]); // is randomized at init, position of images
  let clickedItem = $state(-1); // -1: no image clicked, otherwise index of clicked image

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;
  let playNextAudioTimer: ReturnType<typeof setTimeout>;

  // help both after a set time and requested - levels interact!
  let helpLevel = $state(new HelpLevel(numberOfHelpLevels));

  let imgWiggle = $state([false, false, false, false]);
  let fingerPointing = $state([false, false, false, false]);

  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(false); // help not active for some seconds after activated last time

  // for results:
  let gotHelpForItem = false;
  let hasErrorForItem = false;

  // FOR RESEARCH ONLY
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let responseDetails: Array<ResponseDetail> = [];
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step1 currentState:", currentState);
  });
  $effect(() => {
    logger("Step1 stage:", stage);
  });
  $effect(() => {
    logger("Step1 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  // help functions ---------------------------------------

  const countHelp = () => {
    switch (stage) {
      case 0:
        helpCount.value.comprehension.item11++;
        break;
      case 1:
        helpCount.value.comprehension.item12++;
        break;
      case 2:
        helpCount.value.comprehension.item13++;
        break;
      case 3:
        helpCount.value.comprehension.item14++;
        break;
      default:
        console.error("Step 1, countHelp, unknown stage:", stage);
    }
  };

  // show repeat button a defined time after showing task
  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help with 1 hierarchy but different help timed and after error ---
  const startHelpTimerAndWaitForResponse = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      timedHelp();
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  // image vibrates and get glowing shadow, sentence sound repeated
  const vibrationHelp = () => {
    // set leve for both types of help
    helpLevel.setNextLevel();
    imgWiggle[targets[stage]] = true;
    repeatSentence();
  };

  // finger slids up to correct sentence, sentence sound repeated
  const fingerPointingHelp = () => {
    // After finger-pointing help give correct answer and go on
    helpLevel.setNextLevel();
    fingerPointing[targets[stage]] = true;
    repeatSentence();
  };

  // last help level is to provide correct answer
  const giveAnswerHelp = () => {
    progress.value += 1;
    currentState = State.Correct;
    helpLevel.setNextLevel();
    setVariablesForCorrectResponse();
    // say correct sentence
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentenceOK,
      null,
    );
    // next picture or task
    helpTimer = setTimeout(() => {
      requestedHelpPaused = true;
      clearTimeout(helpTimer);
      afterCorrectResponseOrLastHelpLevel();
    }, studentUserData.value.settings.helpDelaySecs * 500); // half of help delay
  };

  // give help after clicking help button according to help level
  const afterErrorHelp = () => {
    currentState = State.GivingHelp;
    logger("Step1 afterErrorHelp");
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(playNextAudioTimer);
    clearTimeout(helpTimer);
    switch (helpLevel.level) {
      case 1:
      case 2:
        tryAgainSentenceHelp();
        break;
      case 3:
        vibrationHelp();
        break;
      case 4:
        fingerPointingHelp();
        break;
      case 5:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "error in afterErrorHelp() switch, helpLevel: ",
          helpLevel.level,
          " - stage:",
          stage,
        );
    }
  };

  // give help after a duration according to help level
  const timedHelp = () => {
    currentState = State.GivingHelp;
    logger("Step1 timedHelp");
    countHelp();
    // numberOfTimedHelp++;  // FOR RESEARCH ONLY
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    switch (helpLevel.level) {
      case 1:
        repeatSentenceHelp();
        break;
      case 2:
        sayPointToSentenceHelp();
        break;
      case 3:
        vibrationHelp();
        break;
      case 4:
        fingerPointingHelp();
        break;
      case 5:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "Step 1, error in timedHelp() switch, helpLevel: ",
          helpLevel.level,
          " - stage: ",
          stage,
        );
    }
  };

  const requestedHelp = () => {
    currentState = State.RepeatingTask;
    clearTimeout(helpTimer);
    logger("Step1 requestedHelp");
    countHelp();
    // numberOfRequestedHelp++; // FOR RESEARCH ONLY
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      DaDK.Step1Help,
      startHelpTimerAndWaitForResponse,
    );
  };

  const repeatSentence = () => {
    if (currentState === State.WaitAfterEnd) return;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  const repeatSentenceHelp = () => {
    helpLevel.setNextLevel();
    repeatSentence();
  };

  const sayPointToSentenceHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.PointTo, repeatSentence);
  };

  const tryAgainSentenceHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.TryAgain, repeatSentence);
  };

  // logic for going on with task ----------------------------------

  // pause before sentence is played
  const waitAndStart = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    playNextAudioTimer = setTimeout(() => {
      playIntro();
    }, firstWaitSecs);
  };

  const playIntro = (): void => {
    currentState = State.PlayIntro;
    playSoundWithAudioTag(audioTag, DaDK.Step1Intro, waitBeforeTask);
  };

  const waitBeforeTask = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.PresentTask;
    playNextAudioTimer = setTimeout(() => {
      presentShowMe();
    }, firstWaitSecs);
  };

  // play "show me" and go to play target sentence
  const presentShowMe = (): void => {
    playSoundWithAudioTag(audioTag, DaDK.ShowMe, presentTask);
  };

  // play target sentence, set currentState to wait for response
  const presentTask = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      WaitForResponse,
    );
  };

  // image buttons active, repeat sentence button active while waiting for response
  const WaitForResponse = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startRepeatButtonTimer();
    // initializeResultVariables();
    startHelpTimerAndWaitForResponse();
  };

  // correct -----

  // common reset for chosen correct response and given as last stage of help
  const setVariablesForCorrectResponse = () => {
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.comprehensionCorrectFirstTry =
        result.value.comprehensionCorrectFirstTry + 1;
    }
    progress.value += 1;
    progress;
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    allowRepetition = false;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    // attempt = 1; // FOR RESEARCH ONLY
  };

  // play "correct", and go on
  const correctResponse = (): void => {
    currentState = State.Correct;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentenceOK,
      afterCorrectResponseOrLastHelpLevel,
    );
  };

  // go to next target or next step
  const afterCorrectResponseOrLastHelpLevel = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    tasksCompleted.value = tasksCompleted.value + 1;
    if (stage < 3) {
      stage++;
      helpLevel.reset();
      gotHelpForItem = false;
      hasErrorForItem = false;
      waitBeforeTask();
    } else {
      setCompletedBackgroundColor(true);
      currentState = State.WaitAfterEnd;
      // result.value.responseDetails.push(...responseDetails);
      result.value.lastStepCompleted = 1;
      setTimeout(() => {
        route.current = Pages.Step2;
      }, afterCorrectWaitSecs);
    }
  };

  // error ----

  // after error pause before new prompt
  const afterError = (): void => {
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(playNextAudioTimer);
    allowRepetition = true;
    hasErrorForItem = true;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    afterErrorHelp();
  };

  // button event handling ------
  // se also requestedHelp above

  // an image button was clicked, response evaluated, data saved
  const imgClicked = (index: number): void => {
    logger("Step1 clickedItem:", clickedItem);
    clearTimeout(playNextAudioTimer);
    clickedItem = index;
    if (index === targets[stage]) {
      // saveResultVariablesWithCorrect(true);
      currentState = State.Correct;
      setVariablesForCorrectResponse();
      playCorrectRandomWithAudioTag(audioTag, correctResponse);
    } else {
      // saveResultVariablesWithCorrect(false);
      // attempt++;
      currentState = State.Error;
      afterError();
    }
  };

  // repeat sentence (after speaker button press)
  const repeatSound = (): void => {
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++; // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      allowRepetition = true;
      startHelpTimerAndWaitForResponse();
    } else {
      pauseStartTime = Date.now();
      clearTimeout(playNextAudioTimer);
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    currentState === State.WaitAfterEnd;
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimerAndWaitForResponse();
  };

  // FOR RESEARCH ONLY --------------------------------------------

  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production

  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 1,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem: targetVerbAndObjects.value[targets[stage]].sentence,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);
  // initializeResultVariables; // FOR RESEARCH ONLY
  // randomize order pictures are selected for targets
  shuffleArray(targets);
  waitAndStart();
</script>

<!-- HTML ===================================================== -->

<main data-page="step1">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <div
    data-testid="index"
    data-index={targets[stage]}
    style="display: none;"
  ></div>
  <!-- top panel with buttons for help and repeat sentence -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div>
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- 4 clickable image buttons in 2x2 grid -->
  {#each targetVerbAndObjects.value as target, i}
    <button
      data-button-index={i}
      style="background-image: url('{encodeURI(
        Path.ImgBg + target.image + Extension.ImgBg,
      )}'); background-image: image-set('{encodeURI(
        Path.ImgBg + target.image + Extension.WebpBg,
      )}'); background-size: cover;"
      class={"img img" + i}
      class:img-wiggle={imgWiggle[i]}
      class:img-shadow-glow={imgWiggle[i]}
      class:img-wait={currentState !== State.WaitForResponse}
      class:img-correct={(currentState === State.Correct ||
        currentState === State.WaitAfterEnd) &&
        i === targets[stage]}
      class:img-error={currentState === State.Error && clickedItem === i}
      disabled={currentState !== State.WaitForResponse}
      onclick={() => imgClicked(i)}
    >
      <picture>
        <source
          type="image/webp"
          srcset={encodeURI(Path.Img + target.image + Extension.Webp)}
          draggable={false}
        />
        <img
          draggable={false}
          src={encodeURI(Path.Img + target.image + Extension.Png)}
          alt=""
        />
      </picture>
    </button>
    <div
      class={"finger-img finger-img-" + i}
      class:finger-point={fingerPointing[i]}
    >
      <picture class="finger-png">
        <source
          type="image/webp"
          srcset={encodeURI("finger-2.webp")}
          draggable={false}
        />
        <img draggable={false} src={encodeURI("finger-2.png")} alt="" />
      </picture>
    </div>
  {/each}
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ====================================================== -->
<style>
  :root {
    --svg-fill: gray;
  }

  main {
    /* width: 100vw; */
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 49% 49%;
    grid-template-rows: 14% 41% 41% 2%;
    grid-template-areas:
      "top top"
      "img1 img2"
      "img3 img4"
      "prog prog";
    gap: 2%;
    padding: 3%;
    padding-top: 1%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .img0 {
    grid-area: img1;
  }
  .img1 {
    grid-area: img2;
  }
  .img2 {
    grid-area: img3;
  }
  .img3 {
    grid-area: img4;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .finger-png {
    object-position: bottom right;
  }
  .finger-img {
    z-index: 2;
    transform: translate(-5%, 0vh);
    visibility: hidden;
    pointer-events: none;
  }
  .finger-img-0 {
    grid-area: img1;
  }
  .finger-img-1 {
    grid-area: img2;
  }
  .finger-img-2 {
    grid-area: img3;
  }
  .finger-img-3 {
    grid-area: img4;
  }
  /* finger pointing help with animated finger sliding up */
  .finger-point {
    visibility: visible;
    animation-duration: 2s;
    animation-name: slideup;
  }
  @keyframes slideup {
    from {
      transform: translate(-5%, 50vh);
    }
    to {
      transform: translate(-5%, 0vh);
    }
  }
</style>
